/**
 * Platform-aware keyboard shortcut labels (UX-09).
 *
 * The console printed `⌘K` everywhere. On Windows and Linux — which is most of
 * this product's users — that key does not exist, and the glyph is not merely
 * cosmetic: a screen reader announces `⌘` as "place of interest sign", which is
 * worse than useless. The handler already accepted Ctrl; only the LABEL lied.
 *
 * Detection is `navigator.platform`-free: that property is deprecated and lies
 * under emulation. `userAgentData.platform` where available, falling back to a
 * userAgent probe, and defaulting to the non-Mac label — a Mac user shown
 * "Ctrl K" is mildly wrong, a Windows user shown "⌘K" is looking for a key that
 * is not on their keyboard.
 */

/** True on macOS/iPadOS, where the meta key is the convention. */
export function isAppleOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const uaData = (navigator as Navigator & {
    userAgentData?: { platform?: string }
  }).userAgentData
  const platform = uaData?.platform ?? ''
  if (platform) return /mac|ios/i.test(platform)
  return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent ?? '')
}

/**
 * A displayable shortcut, e.g. `⌘K` or `Ctrl+K`.
 *
 * The separator differs on purpose: `⌘K` is how macOS writes it, `Ctrl+K` is how
 * Windows does. Matching each platform's own convention is the point.
 */
export function shortcut(key: string): string {
  return isAppleOS() ? `⌘${key.toUpperCase()}` : `Ctrl+${key.toUpperCase()}`
}

/**
 * The same shortcut spoken properly.
 *
 * Use as `aria-label` wherever the visible label is the glyph — otherwise a
 * screen reader reads `⌘` as "place of interest sign" and the user learns
 * nothing.
 */
export function shortcutLabel(key: string): string {
  return `${isAppleOS() ? 'Command' : 'Control'} ${key.toUpperCase()}`
}
